<template>
  <div>
    <load-profile></load-profile>
    <b-card>
      <b-card-text class="mb-2 d-flex justify-content-between">
        <b-button-group>
          <b-button
            variant="outline-primary"
            @click="handleAccessChange('public')"
            :class="{
              ['bg-primary text-white']: data.accessSpecifier === 'public',
            }"
          >
            {{ $t("button.team") }}
          </b-button>
          <b-button
            variant="outline-primary"
            @click="handleAccessChange('private')"
            :class="{
              ['bg-primary text-white']: data.accessSpecifier === 'private',
            }"
          >
            {{ $t("button.personal") }}
          </b-button>
        </b-button-group>
        <b-button variant="outline-primary" @click="handleCreateGroup">{{
          $t("button.addGroup")
        }}</b-button>
      </b-card-text>
      <DraggableTree
        ref="DraggableTree"
        :data="items"
        :draggable="true"
        :crossTree="false"
        :ondragstart="ondragstart"
        :ondragend="ondragend"
      >
        <template v-slot="{ data, store }">
          <template v-if="!data.isDragPlaceHolder">
            <div class="text-left" v-if="data.children && data.children.length">
              <message
                :item="data"
                :store="store"
                @onCreate="handleCreate"
                @onUpdate="handleUpdate"
                @onDelete="handleDelete"
              />
            </div>
            <div v-else>
              <message
                :item="data"
                :store="store"
                @onCreate="handleCreate"
                @onUpdate="handleUpdate"
                @onDelete="handleDelete"
              />
            </div>
          </template>
        </template>
      </DraggableTree>
    </b-card>

    <b-modal
      ref="modal-form"
      id="modal-form"
      hide-footer
      content-class="shadow"
      :title="$t('breadcrumb.quickMessage')"
    >
      <validation-observer ref="editForm" #default="{ invalid }">
        <b-form
          key="editForm"
          @submit.prevent="validateForm"
          v-shortkey="['ctrl', 'enter']"
          @shortkey="validateForm()"
        >
          <n-input :fields="fields" v-model="data" />
          <b-row>
            <b-col cols="12" class="text-right my-1">
              <b-button
                variant="outline-default"
                type="button"
                class="mr-1"
                @click="hideModal"
              >
                {{ $t("button.cancel") }}
              </b-button>
              <n-button-loading
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="outline-primary"
                :loading="loading"
                :disabled="invalid"
                v-if="$can('update', permissionType)"
              >
                {{ $t("button.save") }}
              </n-button-loading>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BLink,
  BModal,
  BButton,
  BButtonGroup,
  BForm,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import NButtonLoading from "@/components/NButtonLoading";
import NFormConfirmation from "@/components/NFormConfirmation";
import NInput from "@/components/NInput";
import FormInput from "./formInput";
import FormInputGroup from "./formInputGroup";
import Repository from "@/repositories/RepositoryFactory";
import { DraggableTree } from "vue-draggable-nested-tree";
import Message from "./Message.vue";
import { scrollOnMouseMove, unsetScrollOnMouseMove } from "./helper";

const QuickMessageRepository = Repository.get("quickMessage");

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BLink,
    BModal,
    BButtonGroup,
    BButton,
    BForm,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    DraggableTree,
    Message,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: {
        parent: null,
        title: null,
        description: null,
        visible: true,
        accessSpecifier: "private",
      },
      originItems: [],
      items: [],
      drag: false,
      initData: {},
      loading: false,
      oldParent: null,
    };
  },
  mounted() {
    this.show();
    const acs = this.$route.query.acs?.trim();
    if (["private", "public"].includes(acs)) {
      this.data.accessSpecifier = acs;
    }
  },
  methods: {
    showModal() {
      this.$refs["modal-form"].show();
    },
    hideModal() {
      this.$refs["modal-form"].hide();
    },
    handleAccessChange(accessSpecifier) {
      this.data = { ...this.data, accessSpecifier };
      this.updateTree();
      this.$router.replace({ query: { acs: accessSpecifier } });
    },
    ondragstart(data, store) {
      const parent = data.parent;
      if (parent.isRoot) {
        this.items = this.items.map((e) => {
          e.droppable = false;
          return e;
        });
      } else {
        const rootNode = this.items[0].parent;
        rootNode._vm.$el?.classList.add("dragging-child");
      }
      this.oldParent = { ...parent };

      scrollOnMouseMove();
    },
    ondragend(data, store) {
      this.$nextTick(() => {
        const parent = data.parent;

        // Update parent
        if (this.oldParent && parent._id !== this.oldParent?._id) {
          if (!parent?.parent?.isRoot) {
            this.updateTree();
            return;
          }
          data.parentId = parent.id;
          data.loading = true;
          const { parentId, id } = data;
          QuickMessageRepository.update(
            { parent: parentId ? parentId : null },
            id
          )
            .then(() => {
              this.originItems.map((e) => {
                if (e.id === id) {
                  e.parent = parentId;
                }
                return e;
              });
            })
            .catch()
            .then(() => {
              this.hideModal();
              data.loading = false;
            });
        }

        const items = parent.children;
        const index = items.findIndex((e) => e.id === data.id);

        if (index !== data.sorting) {
          const params = items.map((e, i) => ({ id: e.id, sorting: i }));
          this.updateSorting(params, parent);
        }

        if (this.oldParent && this.oldParent.isRoot) {
          this.items = this.items.map((e) => {
            e.droppable = true;
            return e;
          });
        } else {
          const rootNode = this.items[0].parent;
          rootNode._vm.$el?.classList.remove("dragging-child");
        }
      });

      unsetScrollOnMouseMove();
    },
    buildTree(list) {
      var map = {},
        node,
        roots = [],
        i;

      for (i = 0; i < list.length; i += 1) {
        list[i] = { ...list[i] };
        map[list[i].id] = i;
        list[i].parentId = list[i].parent;
        list[i].children = [];
      }

      for (i = 0; i < list.length; i += 1) {
        node = list[i];
        if (node.parent && list[map[node.parent]]) {
          node.droppable = false;
          list[map[node.parent]].children.push(node);
        } else {
          roots.push(node);
        }
      }

      return roots;
    },
    searchTree(items, id) {
      for (let i = 0; i < items.length; i += 1) {
        if (items[i].id == id) {
          return items[i];
        } else if (items[i].children && items[i].children.length > 0) {
          let data = this.searchTree(items[i].children, id);
          if (data) {
            return data;
          }
        }
      }
      return null;
    },
    updateTree() {
      const filterItems = this.originItems
        .filter((e) => e.accessSpecifier === this.data.accessSpecifier)
        .map((e) => {
          const item = this.items.find((i) => i.id === e.id);
          e.open = item ? item.open : false;
          return e;
        });
      this.items.forEach((item) => {
        this.$refs["DraggableTree"].deleteNode(item);
      });
      this.$nextTick(() => {
        this.items = this.buildTree(filterItems.map((e) => ({ ...e })));
      });
    },
    show() {
      this.loading = true;
      QuickMessageRepository.show()
        .then((response) => {
          this.originItems = response.data;
          this.updateTree();
        })
        .catch()
        .then(() => {
          this.loading = false;
        });
    },
    validateForm() {
      this.$refs.editForm.validate().then((success) => {
        if (success) {
          if (!this.data.id) {
            this.create();
          } else {
            this.submit();
          }
        }
      });
    },
    handleCreate(parent) {
      this.parent = parent;
      this.data = { ...this.data, parent: parent?.id };
      this.data.title = null;
      this.data.id = null;
      this.data.description = null;
      this.data.visible = true;
      this.showModal();
      if (parent) {
        this.fields = this.fieldsMessage;
      }
    },
    handleCreateGroup() {
      this.handleCreate(null);
      this.fields = this.fieldsGroup.map((e) => ({ ...e }));
    },
    handleUpdate(data) {
      this.data = data;
      this.showModal();
      console.log(data);
      this.fields =
        data.parent && !data.parent.isRoot
          ? this.fieldsMessage.map((e) => ({ ...e }))
          : this.fieldsGroup.map((e) => ({ ...e }));
    },
    handleDelete(data) {
      this.data = data;
      this.confirmDelete(data.id);
    },
    submit() {
      this.loading = true;
      this.data.loading = true;
      const { visible, title, description, id, parentId } = this.data;
      QuickMessageRepository.update(
        { visible, title, description, parent: parentId },
        id
      )
        .then((response) => {
          const data = this.searchTree(this.items, id);
          data.parentId = parentId;
          data.visible = visible;
          data.title = title;
          data.description = description;

          this.originItems = this.originItems.map((e) => {
            if (e.id === id) {
              return response.data.data;
            } else {
              return e;
            }
          });
        })
        .catch()
        .then(() => {
          this.hideModal();
          this.loading = false;
          this.data.loading = false;
        });
    },
    create() {
      this.loading = true;
      const { visible, title, description, id, parent, accessSpecifier } =
        this.data;
      const thisParent = this.searchTree(this.items, parent);
      const sorting = thisParent
        ? thisParent.children.length
        : this.items.length;
      QuickMessageRepository.create({
        visible,
        title,
        description,
        id,
        parent,
        accessSpecifier,
        sorting,
      })
        .then((response) => {
          const data = response.data;
          this.originItems.push({ ...data });
          this.updateTree();
        })
        .catch()
        .then(() => {
          this.hideModal();
          this.loading = false;
        });
    },
    delete(node) {
      this.loading = true;
      QuickMessageRepository.delete(node.id)
        .then((response) => {
          const originIndex = this.originItems.findIndex(
            (e) => e.id === node.id
          );
          if (originIndex > -1) {
            this.originItems.splice(originIndex, 1);
            this.$refs["DraggableTree"].deleteNode(node);
          }
        })
        .catch()
        .then(() => {
          this.hideModal();
          this.loading = false;
        });
    },
    updateSorting(params, parent) {
      this.loading = true;
      parent._vm.$el?.classList.add("loading-children");
      QuickMessageRepository.bulkUpdate(params)
        .then((response) => {
          this.originItems = this.originItems.map((e) => {
            const param = params.find((i) => i.id === e.id);
            if (param) {
              e.sorting = param.sorting;
            }
            return e;
          });
        })
        .catch()
        .then(() => {
          this.loading = false;
          parent._vm.$el?.classList.remove("loading-children");
        });
    },
    confirmDelete(id) {
      const node = this.searchTree(this.items, id);
      this.$bvModal
        .msgBoxConfirm(
          this.$t("alert.unableToRevertAfterDelete", {
            item: node.title,
          }),
          {
            title: this.$t("button.confirm") + "!",
            size: "sm",
            buttonSize: "sm",
            okVariant: "primary",
            okTitle: this.$t("button.delete"),
            cancelTitle: this.$t("button.cancel"),
            cancelVariant: "outline-secondary",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.delete(node);
          }
        });
    },
    back() {
      this.$router.back();
    },
  },
  setup() {
    const fieldsMessage = FormInput;
    const fieldsGroup = FormInputGroup;
    const permissionType = "chat-settings"; //"modify-quick-message";

    return {
      fields: fieldsMessage.map((e) => ({ ...e })),
      fieldsMessage,
      fieldsGroup,
      permissionType,
    };
  },
};
</script>

<style lang="scss">
.he-tree {
  padding: 0;

  & > .tree-node {
    &.dragging-child {
      & > .tree-node-children {
        & > .draggable-placeholder {
          display: none !important;
        }
      }
    }

    & > div {
      & > .tree-node {
        border: none;
        margin-bottom: 16px;
        border-radius: 8px;
        box-shadow: 3px 1px 8px 3px #e8e8e8;

        .tree-node-inner {
          padding: 8px;
        }

        .draggable-placeholder {
          display: block;
        }

        .tree-node .tree-node-inner-back {
          padding-bottom: 8px;
          padding-right: 8px;
        }
      }

      .tree-node-children {
        padding: 0 0 0 30px;

        @media (max-width: 768px) {
          padding: 0px;
        }

        h4 {
          font-size: 0.95rem;
        }
      }
    }
  }

  .tree-node-inner-back {
    margin-bottom: 0 !important;
    @media (max-width: 768px) {
      padding-left: 0px !important;
    }

    h4 {
      font-size: 1.1rem;
    }

    @media (max-width: 420px) {
      h4 {
        font-size: 1rem;
      }
    }

    h6 {
      font-size: 0.9rem;
    }

    .tree-node-inner {
      padding: 0px !important;
    }
  }
}

.tree-node-inner {
  padding: 0;
  cursor: move;
}

.draggable-placeholder-inner {
  border: 1px dashed #0088f8;
  box-sizing: border-box;
  background: rgba(0, 136, 249, 0.09);
  color: #0088f9;
  text-align: center;
  padding: 0;
  display: flex;
  align-items: center;
}
</style>
